/**
 * AppAndVoicesSection — Iter 71.
 *
 * Combines the patient + staff "Install the app" CTAs alongside the rotating
 * patient-voices testimonial in a single section. Layout:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  FOR PATIENTS              │           FOR STAFF             │
 *   │  install card              │           install card          │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │             ← sliding patient review →                       │
 *   │                    ★ ★ ★ ★ ★ (avg 4.8)                       │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Renders nothing in `reviews-only` mode if both the app is already installed
 * and no reviews exist, so we don't show an empty section.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Smartphone, Apple, Share, Download, Info, MoreVertical, Star, ChevronLeft, ChevronRight, Users, Briefcase } from 'lucide-react';

const ROTATE_MS = 5500;

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export default function AppAndVoicesSection({ reviews = [] }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [modal, setModal] = useState(null);
  const platform = detectPlatform();

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setModal('success'); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const tryInstall = async (audience) => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') setModal('success');
      setDeferred(null);
      return;
    }
    if (platform === 'ios') setModal('ios');
    else if (platform === 'android') setModal('android');
    else setModal({ kind: 'desktop', audience });
  };

  // Reviews carousel
  const list = useMemo(() => reviews.slice(0, 12), [reviews]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const swipeRef = useRef(null);

  useEffect(() => {
    if (paused || list.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % list.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, list.length]);

  if (installed && list.length === 0) return null;

  const r = list[idx] || null;
  const rating = r ? Math.max(1, Math.min(5, parseInt(r.rating) || 5)) : 5;
  const text = (r?.text || r?.comment || '').trim();
  const name = r?.author_name || r?.patient_name || 'Verified patient';
  const avg = list.length ? (list.reduce((s, x) => s + (x.rating || 5), 0) / list.length).toFixed(1) : '5.0';
  const avgRounded = Math.round(parseFloat(avg));

  const next = () => setIdx(i => (i + 1) % list.length);
  const prev = () => setIdx(i => (i - 1 + list.length) % list.length);

  const onPointerDown = (e) => { swipeRef.current = e.clientX; };
  const onPointerUp = (e) => {
    if (swipeRef.current == null) return;
    const dx = e.clientX - swipeRef.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    swipeRef.current = null;
  };

  return (
    <section className="py-14 md:py-20 px-6 md:px-10" style={{ background: '#FBF7EE' }} data-testid="app-voices-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#003D80] font-bold mb-2">GET THE APP · LOVED BY USERS</div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-[#003D80]">
            One install for everyone.
          </h2>
          <p className="text-sm md:text-base text-[#3A4A5E] mt-3 max-w-2xl mx-auto">
            Whether you're a patient booking a session or a clinician running the schedule — installs to your phone in seconds, no app store needed.
          </p>
        </div>

        {/* Two install cards side-by-side */}
        {!installed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-10" data-testid="install-cards">
            <InstallCard
              audience="patients"
              tagline="For patients"
              title="Book, message, and follow your program"
              points={['Book and reschedule in one tap', 'Receive your exercise program with videos', 'Track recovery with built-in surveys']}
              icon={Users}
              accent="#FFB000"
              onInstall={() => tryInstall('patients')}
              testid="install-card-patients"
            />
            <InstallCard
              audience="staff"
              tagline="For staff"
              title="Charts, schedule and AI scribe at your fingertips"
              points={['Patient charts wherever you go', 'Dictate notes from the treatment room', 'Drag-and-drop calendar on mobile']}
              icon={Briefcase}
              accent="#003D80"
              onInstall={() => tryInstall('staff')}
              testid="install-card-staff"
            />
          </div>
        )}

        {/* Sliding review — centered below both cards */}
        {list.length > 0 && (
          <div className="max-w-2xl mx-auto" data-testid="voices-block"
               onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
               onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
            <div className="relative">
              <div key={r?.review_id || idx}
                   className="bg-white rounded-2xl border border-[#E8DFC8] p-6 md:p-7 shadow-sm text-center"
                   data-testid="voices-card">
                <svg className="w-7 h-7 text-[#FFB000] mx-auto mb-3 opacity-80" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9.13,17.66c-0.78,1.49-2.69,2.32-4.45,1.94c0,0,4.46-7.41,4.46-12.45c0-2.21,1.79-4,4-4v3.65 C13.14,6.81,12.04,7.93,12.04,9.27c0,1.95,1.59,3.5,3.5,3.5C16.6,15.16,12.04,17.66,9.13,17.66z"/>
                </svg>
                <p className="text-base md:text-lg leading-relaxed text-[#0F1B2D] mb-4" data-testid="voices-text">
                  {text || 'Excellent care — the booking flow is the smoothest I have used.'}
                </p>
                <div className="text-sm text-[#003D80] font-semibold" data-testid="voices-author">
                  — {name}
                </div>
              </div>
              {list.length > 1 && (
                <>
                  <button onClick={prev} aria-label="Previous review"
                          data-testid="voices-prev"
                          className="absolute left-0 top-1/2 -translate-x-3 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-[#E8DFC8] flex items-center justify-center text-[#003D80] hover:bg-[#003D80] hover:text-white transition shadow-sm">
                    <ChevronLeft className="w-4 h-4"/>
                  </button>
                  <button onClick={next} aria-label="Next review"
                          data-testid="voices-next"
                          className="absolute right-0 top-1/2 translate-x-3 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-[#E8DFC8] flex items-center justify-center text-[#003D80] hover:bg-[#003D80] hover:text-white transition shadow-sm">
                    <ChevronRight className="w-4 h-4"/>
                  </button>
                </>
              )}
            </div>

            {/* Star grade — centered, below the review */}
            <div className="flex flex-col items-center mt-5" data-testid="voices-stars-block">
              <div className="flex items-center gap-1" data-testid="voices-avg-stars">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className="w-5 h-5"
                        fill={s <= avgRounded ? '#FFB000' : 'none'}
                        stroke="#FFB000"
                        strokeWidth={2}/>
                ))}
              </div>
              <div className="text-sm font-bold text-[#0F1B2D] mt-1">{avg} <span className="text-[#3A4A5E] font-normal">/ 5.0</span></div>
              <div className="text-[11px] uppercase tracking-wider text-[#3A4A5E] mt-0.5">
                Based on {list.length} verified review{list.length === 1 ? '' : 's'}
              </div>
            </div>

            {list.length > 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="voices-dots">
                {list.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)}
                          aria-label={`Go to review ${i + 1}`}
                          className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-5 bg-[#003D80]' : 'w-1.5 bg-[#E8DFC8]'}`}/>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {modal === 'ios' && <IosModal onClose={() => setModal(null)}/>}
      {modal === 'android' && <AndroidModal onClose={() => setModal(null)}/>}
      {modal && typeof modal === 'object' && modal.kind === 'desktop' && <DesktopModal onClose={() => setModal(null)}/>}
      {modal === 'success' && <SuccessModal onClose={() => setModal(null)}/>}
    </section>
  );
}

function InstallCard({ tagline, title, points, icon: Icon, accent, onInstall, testid }) {
  return (
    <div className="relative bg-white rounded-2xl border border-[#E8DFC8] p-6 md:p-7 flex flex-col gap-3 overflow-hidden hover:shadow-lg transition" data-testid={testid}>
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }}/>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: accent }}>{tagline}</span>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md" style={{ background: accent }}>
          <Icon className="w-5 h-5" strokeWidth={1.75}/>
        </div>
      </div>
      <h3 className="text-lg md:text-xl font-bold tracking-tight text-[#003D80] leading-snug">{title}</h3>
      <ul className="space-y-1.5 mb-2">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#3A4A5E]">
            <span className="inline-block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: accent }}/>
            {p}
          </li>
        ))}
      </ul>
      <button onClick={onInstall}
              data-testid={`${testid}-cta`}
              className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition"
              style={{ background: accent }}>
        <Download className="w-4 h-4"/> Install the app
      </button>
    </div>
  );
}

function ModalShell({ icon: Icon, title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-soft flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#003D80] text-white flex items-center justify-center">
              <Icon className="w-4 h-4"/>
            </div>
            <h3 className="font-heading font-bold text-[#0F1B2D]">{title}</h3>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg" data-testid={`${testid}-close`}>×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function IosModal({ onClose }) {
  return (
    <ModalShell icon={Apple} title="Install on iPhone / iPad" onClose={onClose} testid="install-ios-modal">
      <p className="text-sm text-fg-secondary mb-4">Open in Safari, then:</p>
      <ol className="space-y-3 text-sm">
        <li className="flex gap-3"><Step n={1}/> Tap <strong>Share</strong> <Share className="w-3.5 h-3.5 inline-block mb-0.5 text-[#003D80]"/></li>
        <li className="flex gap-3"><Step n={2}/> Scroll down and tap <strong>Add to Home Screen</strong></li>
        <li className="flex gap-3"><Step n={3}/> Tap <strong>Add</strong> in the top-right corner</li>
      </ol>
    </ModalShell>
  );
}

function AndroidModal({ onClose }) {
  return (
    <ModalShell icon={Smartphone} title="Install on Android" onClose={onClose} testid="install-android-modal">
      <p className="text-sm text-fg-secondary mb-4">In Chrome or Edge:</p>
      <ol className="space-y-3 text-sm">
        <li className="flex gap-3"><Step n={1}/> Tap menu <MoreVertical className="w-3.5 h-3.5 inline-block mb-0.5 text-[#003D80]"/> (top right)</li>
        <li className="flex gap-3"><Step n={2}/> Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
        <li className="flex gap-3"><Step n={3}/> Confirm — Physiogenic is now installed</li>
      </ol>
    </ModalShell>
  );
}

function DesktopModal({ onClose }) {
  const url = typeof window !== 'undefined' ? window.location.origin : 'https://physiogenic.app';
  return (
    <ModalShell icon={Smartphone} title="The app is best on mobile" onClose={onClose} testid="install-desktop-modal">
      <p className="text-sm text-fg-secondary mb-3">Open this URL on your phone:</p>
      <div className="bg-[#FFF9EC] p-3 rounded-lg text-center mb-3">
        <code className="text-sm font-mono text-[#003D80] break-all">{url}</code>
      </div>
      <p className="text-xs text-fg-muted">Your browser will then show "Install the app" on the home page.</p>
    </ModalShell>
  );
}

function SuccessModal({ onClose }) {
  return (
    <ModalShell icon={Info} title="You're all set 🎉" onClose={onClose} testid="install-success-modal">
      <p className="text-sm text-fg-secondary">Physiogenic is on your home screen. Tap the icon to open fullscreen.</p>
      <button onClick={onClose} className="w-full mt-4 btn-primary text-sm">Got it</button>
    </ModalShell>
  );
}

function Step({ n }) {
  return <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003D80] text-white text-xs font-bold flex items-center justify-center">{n}</span>;
}
